import { useTranslation } from 'react-i18next'

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { MetChange } from '@/utils/service-diff.utils'

type Props = {
  change?: MetChange
}

/**
 * Small badge shown next to a Hitta stöd recommendation indicating how it
 * changed since the last regeneration run. Renders nothing when unchanged or
 * newly generated for the first time — only an actual status change is
 * worth calling out, "new" fires for every group at once on first generation.
 */
export const ChangeBadge = ({ change }: Props) => {
  const { t } = useTranslation()

  if (!change || change.kind === 'unchanged' || change.kind === 'new') return null

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center whitespace-nowrap rounded-full bg-brand-pion px-3 py-1 text-sm font-medium text-brand-mullbar">
            {t('servicesPage.changes.previously', { value: t(`global.${metKey(change.from)}`) })}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p>{t('servicesPage.changes.changedTooltip')}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

// Maps a met value to the existing global translation key.
const MET_LABEL_KEY = {
  not_recommended: 'notRecommended',
  recommended: 'recommended',
  uncertain: 'uncertain',
} as const

function metKey(met: 'recommended' | 'uncertain' | 'not_recommended') {
  return MET_LABEL_KEY[met]
}

export default ChangeBadge
